import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Competition } from '@/types/domain';
import type { SportFilterValue } from '@/components/shared/SportFilter';
import CompetitionGameList from './CompetitionGameList';
import { filterCompetitionsBySport } from './filterCompetitionsBySport';

export interface LobbyTabProps {
  competitions: Competition[];
  selectedSport: SportFilterValue;
  isLoading: boolean;
  showBodyAds: boolean;
  onGameAction: (gameId: string) => void;
  onViewLeaderboard: (gameId: string) => void;
  onMenuClick: (gameId: string) => void;
}

/**
 * Lobby tab — all non-final contests, including ones already entered (DKPLAT-449).
 */
const LobbyTab: React.FC<LobbyTabProps> = ({
  competitions,
  selectedSport,
  isLoading,
  showBodyAds,
  onGameAction,
  onViewLeaderboard,
  onMenuClick,
}) => {
  const { t } = useTranslation();

  const games = useMemo(() => {
    return filterCompetitionsBySport(competitions, selectedSport)
      .filter(g => g.state !== 'final')
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) {
          return a.isOpen ? -1 : 1;
        }
        return a.gameDate.getTime() - b.gameDate.getTime();
      });
  }, [competitions, selectedSport]);

  return (
    <CompetitionGameList
      games={games}
      isLoading={isLoading}
      skeletonCount={5}
      emptyTitle={t('pages.noGamesAvailable')}
      emptySubtitle={t('pages.noGamesAvailableSubtitle')}
      showBodyAds={showBodyAds}
      variant="lobby"
      onGameAction={onGameAction}
      onViewLeaderboard={onViewLeaderboard}
      onMenuClick={onMenuClick}
    />
  );
};

export default LobbyTab;
